import {useMemo} from 'react';
import {NavigateFunction, useLocation, useNavigate} from 'react-router';
import {updateSearchString} from './updateSearch';

type SearchSchema = {
    [key: string]: {
        [key: string]: string;
    };
};

export function useSearchModel<T extends SearchSchema>(model: T) {
    const location = useLocation();
    const navigate = useNavigate();

    return useMemo(() => decodeSearchParams(location.search), [location.search]);

    function decodeSearchParams(locationSearch: string) {
        const searchParams = Object.fromEntries(new URLSearchParams(locationSearch).entries());

        let shouldNavigate = false;
        for (const key of Object.keys(searchParams).filter(key => key in model && model[key])) {
            const variants = Object.values(model[key]!);
            const isCorrect = variants.some(variant => variant == searchParams[key]);
            if (!isCorrect) {
                shouldNavigate = true;
                delete searchParams[key];
            }
        }

        const search = new URLSearchParams(searchParams).toString();
        const params = searchParams as {
            [K in keyof T]: T[K][keyof T[K]];
        };

        const syncEffect = () => {
            if (shouldNavigate) {
                navigate({...location, search: search}, {replace: true});
            }
        };
        const syncDependencyList = [shouldNavigate, search];

        const controls = {
            ...createSearchControls(model, navigate, location.search),
            sync: [syncEffect, syncDependencyList] as const,
        };
        return [params, controls] as const;
    }
}

function createSearchControls<T extends SearchSchema>(schema: T, navigate: NavigateFunction, currentSearch: string) {
    const goTos = Object.fromEntries(
        Object.keys(schema).map(paramName => [
            paramName,
            Object.fromEntries(
                Object.keys(schema[paramName]).map(paramVariantKey => {
                    const paramValue = schema[paramName][paramVariantKey];
                    const goToParam = () => {
                        if (new URLSearchParams(currentSearch).get(paramName) !== paramValue) {
                            navigate({search: updateSearchString(currentSearch, {[paramName]: paramValue})});
                        }
                    };
                    return [paramVariantKey, goToParam];
                })
            ),
        ])
    ) as {
        [I in keyof T]: {
            [J in keyof T[I]]: () => void;
        };
    };

    const undos = Object.fromEntries(
        Object.keys(schema).map(paramName => {
            const goBackFromParam = () => {
                if (new URLSearchParams(currentSearch).has(paramName)) {
                    navigate(-1);
                }
            };
            return [paramName, goBackFromParam];
        })
    ) as {
        [I in keyof T]: () => void;
    };

    return {
        goTo: goTos,
        undo: undos,
    };
}
